import { ITenant, TenantStatusEnum } from '@monetize/types/app';
import { MdMoreVert } from 'react-icons/md';
import {
  MButton,
  MDropdownActionItem,
  MDropdownActions,
  MFlex,
  MIconAction,
  MIDCopyBox,
  MTag,
  MText,
} from '../../../components/Monetize';
import { useFlags } from '../../../services/launchDarkly';

interface TenantCardProps {
  origin?: 'modal' | 'page';
  tenant: ITenant;
  isCurrentTenant?: boolean;
  onClick: (tenant: ITenant) => void;
  onAccept: (tenant: ITenant) => void;
  onEdit?: (tenant: ITenant) => void;
  onDelete?: (tenant: ITenant) => void;
}

export const TenantCard = ({
  origin = 'modal',
  tenant,
  isCurrentTenant,
  onClick,
  onAccept,
  onEdit,
  onDelete,
}: TenantCardProps) => {
  const { allowTenantDeletion } = useFlags();
  const isDisabled = tenant.status === TenantStatusEnum.INVITED;

  return (
    <MFlex
      // Keyboard-activatable region. Not a <button> because the card contains
      // nested interactive elements (copy-id button, action menu) and nested
      // <button>s are invalid. role/tabIndex/onKeyDown gives equivalent a11y.
      role={isDisabled ? undefined : 'button'}
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      aria-label={`Switch to ${tenant.name}`}
      onKeyDown={(e) => {
        if (isDisabled) {
          return;
        }
        // Only handle keys that came directly to the card. Without this,
        // pressing Enter / Space on a nested 3-dot button or Accept button
        // would bubble up here and switch tenants instead of activating the
        // child. Card text isn't focusable so it never lands here directly.
        if (e.target !== e.currentTarget) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(tenant);
        }
      }}
      justify="space-between"
      p="4"
      borderWidth="1px"
      borderColor={isCurrentTenant ? 'tIndigo.base' : 'tGray.back'}
      boxSizing="border-box"
      borderRadius="md"
      // Removed `overflow="hidden"` because it clipped the focus halo on
      // the current-tenant card (and any focused tenant card more
      // generally). The card's content is already bounded by its layout —
      // the overflow rule was over-protective.
      color="tPurple.base"
      _hover={{
        borderColor: 'tBlue.lightShade',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
      _focusVisible={{
        outline: 'none',
        boxShadow: 'focus',
        borderColor: 'tIndigo.base',
      }}
      onClick={(e) => {
        if (isDisabled) {
          return;
        }
        // Skip clicks that originate on a nested interactive element
        // (Accept button, 3-dot menu). Plain card text/background still
        // triggers the tenant switch.
        if ((e.target as HTMLElement).closest('button, [role="menuitem"]')) {
          return;
        }
        onClick(tenant);
      }}
      data-testid="tenant-item"
    >
      <MFlex direction="column" w="full" align="start">
        <MFlex justify="start" align="center">
          <MText
            fontSize="md"
            fontWeight="semibold"
            color={isDisabled ? 'tGray.lightPurple' : 'tPurple.dark'}
          >
            {tenant.name}
          </MText>
          <MFlex align="center">
            <MTag
              size="xs"
              variant={tenant.testTenant ? 'orange' : 'purple'}
              ml="2"
            >
              {tenant.testTenant ? 'Test' : 'Live'}
            </MTag>

            {isCurrentTenant && (
              <MTag variant="blue" whiteSpace="nowrap" ml="2">
                Current Tenant
              </MTag>
            )}

            {isDisabled && (
              <MTag variant="blue" ml="2" minWidth="14">
                Invited
              </MTag>
            )}
          </MFlex>
        </MFlex>
        <MFlex align="center" mt={1}>
          <MIDCopyBox
            copyValue={tenant.id!}
            displayIcon
            isDisabled={isDisabled}
            fontWeight="normal"
          />
        </MFlex>
      </MFlex>
      <MFlex align="start">
        {isDisabled && (
          <MButton
            variant="primary"
            width={70}
            minWidth={70}
            height="32px"
            minHeight="32px"
            ml="2"
            onClick={() => onAccept(tenant)}
          >
            Accept
          </MButton>
        )}

        {origin === 'page' && !isDisabled && tenant.isAdmin && (
          <MDropdownActions
            mt={0}
            menuButtonProps={{
              'aria-label': `${tenant.name} actions`,
              'data-testid': `${tenant.name}-tenant-more-btn`,
            }}
            renderTrigger={() => <MIconAction icon={MdMoreVert} />}
          >
            <MDropdownActionItem onClick={() => onEdit && onEdit(tenant)}>
              <MText
                color="inherit"
                data-testid={`${tenant.name}-tenant-rename-btn`}
              >
                Edit
              </MText>
            </MDropdownActionItem>

            {allowTenantDeletion && tenant.testTenant && (
              <MDropdownActionItem
                onClick={() => onDelete && onDelete(tenant)}
                color="tRed.base"
                data-testid={`${tenant.name}-tenant-delete-btn`}
              >
                <MText color="inherit">Delete</MText>
              </MDropdownActionItem>
            )}
          </MDropdownActions>
        )}
      </MFlex>
    </MFlex>
  );
};
